import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Settings as SettingsIcon, Mail, Cloud, MessageSquare,
  Save, RefreshCw, Eye, EyeOff, Check, X,
  AlertCircle, Loader2, Shield, Key, FileText, Lock,
  Bell, Send, Clock, Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import RichTextEditor from '../components/RichTextEditor';
import configService from '../services/configService';
import { FormLoader } from '../components/CommonLoader';
import { SaveButton } from '../components/CommonButtons';
import notificationService from '../services/notificationService';

const SystemConfig = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [configsLoaded, setConfigsLoaded] = useState(false);

  // Mail Settings State
  const [mailSettings, setMailSettings] = useState({
    MAIL_MAILER: 'smtp',
    MAIL_HOST: '',
    MAIL_PORT: '',
    MAIL_USERNAME: '',
    MAIL_PASSWORD: '',
    MAIL_FROM_ADDRESS: '',
    MAIL_FROM_NAME: '',
    MAIL_ENCRYPTION: 'TLS'
  });

  // AWS Settings State
  const [awsSettings, setAwsSettings] = useState({
    AWS_ACCESS_KEY_ID: '',
    AWS_SECRET_ACCESS_KEY: '',
    AWS_DEFAULT_REGION: '',
    AWS_BUCKET: '',
    AWS_USE_PATH_STYLE_ENDPOINT: false
  });

  // MSG91 Settings State
  const [msg91Settings, setMsg91Settings] = useState({
    MSG91_AUTH_KEY: '',
    MSG91_SENDER_ID: '',
    MSG91_ROUTE: '4',
    MSG91_COUNTRY: '91'
  });

  // Pusher Settings State
  const [pusherSettings, setPusherSettings] = useState({
    PUSHER_APP_ID: '',
    PUSHER_APP_KEY: '',
    PUSHER_APP_SECRET: '',
    PUSHER_APP_CLUSTER: 'mt1',
    PUSHER_APP_ENCRYPTED: true,
    PUSHER_USE_TLS: true
  });

  // Email Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    EMAIL_NOTIFICATIONS_ENABLED: true,
    EMAIL_NOTIFICATIONS_FREQUENCY: '24h',
    EMAIL_NOTIFICATIONS_TIME_OF_DAY: '09:00'
  });
  
  const [testingNotification, setTestingNotification] = useState(false);
  const [testingBrowserNotification, setTestingBrowserNotification] = useState(false);

  // General Settings State
  const [generalSettings, setGeneralSettings] = useState({
    '2FA_LOGIN_REQUIRED': false,
    NDA_TEXT: '',
    WATERMARK_FONT_SIZE: '14'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      // Fetch all configs from the API
      const response = await configService.getAllConfigs();
      
      // The configService already returns response.data, so use response directly
      const apiData = response || {};
      
      // Initialize config objects with default values
      const newMailSettings = {
        MAIL_MAILER: 'smtp',
        MAIL_HOST: '',
        MAIL_PORT: '',
        MAIL_USERNAME: '',
        MAIL_PASSWORD: '',
        MAIL_FROM_ADDRESS: '',
        MAIL_FROM_NAME: '',
        MAIL_ENCRYPTION: 'TLS'
      };
      const newAwsSettings = {
        AWS_ACCESS_KEY_ID: '',
        AWS_SECRET_ACCESS_KEY: '',
        AWS_DEFAULT_REGION: '',
        AWS_BUCKET: '',
        AWS_USE_PATH_STYLE_ENDPOINT: false
      };
      const newMsg91Settings = {
        MSG91_AUTH_KEY: '',
        MSG91_SENDER_ID: '',
        MSG91_ROUTE: '4',
        MSG91_COUNTRY: '91'
      };
      const newPusherSettings = {
        PUSHER_APP_ID: '',
        PUSHER_APP_KEY: '',
        PUSHER_APP_SECRET: '',
        PUSHER_APP_CLUSTER: 'mt1',
        PUSHER_APP_ENCRYPTED: true,
        PUSHER_USE_TLS: true
      };
      const newNotificationSettings = {
        EMAIL_NOTIFICATIONS_ENABLED: true,
        EMAIL_NOTIFICATIONS_FREQUENCY: '24h',
        EMAIL_NOTIFICATIONS_TIME_OF_DAY: '09:00'
      };
      const newGeneralSettings = {
        '2FA_LOGIN_REQUIRED': false,
        NDA_TEXT: '',
        WATERMARK_FONT_SIZE: '14'
      };
      
      // The API returns a flat object with config keys directly
      if (typeof apiData === 'object' && apiData !== null) {
        Object.keys(apiData).forEach(key => {
          const value = apiData[key];
          
          // Convert value to appropriate type, handling null
          let processedValue = '';
          
          // Handle null values properly
          if (value === null || value === undefined) {
            processedValue = '';
          } else if (key === 'AWS_USE_PATH_STYLE_ENDPOINT' || key === '2FA_LOGIN_REQUIRED' || key === 'PUSHER_APP_ENCRYPTED' || key === 'PUSHER_USE_TLS' || key === 'EMAIL_NOTIFICATIONS_ENABLED') {
            // Special handling for boolean fields - properly convert string "0" or "1" to boolean
            processedValue = value === '1' || value === 1 || value === true || value === 'true';
          } else {
            processedValue = String(value);
          }
          
          
          // Categorize by prefix or specific keys
          if (key.startsWith('MAIL_')) {
            newMailSettings[key] = processedValue;
          } else if (key.startsWith('AWS_')) {
            if (key === 'AWS_USE_PATH_STYLE_ENDPOINT') {
              newAwsSettings[key] = processedValue; // boolean
            } else {
              newAwsSettings[key] = processedValue; // string
            }
          } else if (key.startsWith('MSG91_')) {
            newMsg91Settings[key] = processedValue;
          } else if (key.startsWith('PUSHER_')) {
            if (key === 'PUSHER_APP_ENCRYPTED' || key === 'PUSHER_USE_TLS') {
              newPusherSettings[key] = processedValue; // boolean
            } else {
              newPusherSettings[key] = processedValue; // string
            }
          } else if (key.startsWith('EMAIL_NOTIFICATIONS_')) {
            if (key === 'EMAIL_NOTIFICATIONS_ENABLED') {
              newNotificationSettings[key] = processedValue; // boolean
            } else {
              newNotificationSettings[key] = processedValue; // string
            }
          } else if (key === '2FA_LOGIN_REQUIRED' || key === 'NDA_TEXT' || key === 'WATERMARK_FONT_SIZE') {
            // For NDA_TEXT, preserve HTML content as-is
            if (key === 'NDA_TEXT') {
              newGeneralSettings[key] = value || ''; // Use original value, not processedValue
            } else {
              newGeneralSettings[key] = processedValue;
            }
          }
        });
      }
      
      
      // Update all states at once - use functional updates to ensure React sees the change
      setMailSettings(() => {
        return newMailSettings;
      });
      setAwsSettings(() => {
        return newAwsSettings;
      });
      setMsg91Settings(() => {
        return newMsg91Settings;
      });
      setPusherSettings(() => {
        
        // Save Pusher config to localStorage when loaded from API
        if (newPusherSettings.PUSHER_APP_KEY) {
          const pusherConfigForStorage = {
            PUSHER_APP_KEY: newPusherSettings.PUSHER_APP_KEY,
            PUSHER_APP_CLUSTER: newPusherSettings.PUSHER_APP_CLUSTER || 'mt1',
            PUSHER_USE_TLS: newPusherSettings.PUSHER_USE_TLS,
            PUSHER_APP_ENCRYPTED: newPusherSettings.PUSHER_APP_ENCRYPTED
          };
          localStorage.setItem('pusher_config', JSON.stringify(pusherConfigForStorage));
          
          // Reinitialize Laravel Echo with new config
          import('../services/laravelEcho').then(module => {
            const laravelEcho = module.default;
            laravelEcho.reinitialize();
          });
        }
        
        return newPusherSettings;
      });
      setNotificationSettings(() => {
        // Ensure EMAIL_NOTIFICATIONS_ENABLED has a boolean value, defaulting to true if not set
        if (newNotificationSettings.EMAIL_NOTIFICATIONS_ENABLED === undefined || newNotificationSettings.EMAIL_NOTIFICATIONS_ENABLED === null) {
          newNotificationSettings.EMAIL_NOTIFICATIONS_ENABLED = true;
        }
        return newNotificationSettings;
      });
      setGeneralSettings(() => {
        return newGeneralSettings;
      });
      setConfigsLoaded(true);
      
    } catch (error) {
      // Don't show error toast if API is not ready yet
      if (error.response?.status !== 404) {
        toast.error('Failed to load configurations');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let configsToUpdate = {};
      
      if (activeTab === 'general') {
        // Convert boolean to string for API, preserve HTML content
        configsToUpdate = {
          ...generalSettings,
          '2FA_LOGIN_REQUIRED': generalSettings['2FA_LOGIN_REQUIRED'] ? '1' : '0',
          NDA_TEXT: generalSettings.NDA_TEXT || '' // Ensure NDA_TEXT is included
        };
      } else if (activeTab === 'mail') {
        configsToUpdate = { ...mailSettings };
      } else if (activeTab === 'aws') {
        // Convert boolean to string for API
        configsToUpdate = {
          ...awsSettings,
          AWS_USE_PATH_STYLE_ENDPOINT: awsSettings.AWS_USE_PATH_STYLE_ENDPOINT ? '1' : '0'
        };
      } else if (activeTab === 'msg91') {
        configsToUpdate = { ...msg91Settings };
      } else if (activeTab === 'pusher') {
        // Convert boolean to string for API
        configsToUpdate = {
          ...pusherSettings,
          PUSHER_APP_ENCRYPTED: pusherSettings.PUSHER_APP_ENCRYPTED ? '1' : '0',
          PUSHER_USE_TLS: pusherSettings.PUSHER_USE_TLS ? '1' : '0'
        };
      } else if (activeTab === 'notifications') {
        // Convert boolean to string for API
        configsToUpdate = {
          ...notificationSettings,
          EMAIL_NOTIFICATIONS_ENABLED: notificationSettings.EMAIL_NOTIFICATIONS_ENABLED ? '1' : '0'
        };
        
        // Save Pusher config to localStorage for frontend use
        const pusherConfigForStorage = {
          PUSHER_APP_KEY: pusherSettings.PUSHER_APP_KEY,
          PUSHER_APP_CLUSTER: pusherSettings.PUSHER_APP_CLUSTER || 'mt1',
          PUSHER_USE_TLS: pusherSettings.PUSHER_USE_TLS,
          PUSHER_APP_ENCRYPTED: pusherSettings.PUSHER_APP_ENCRYPTED
        };
        localStorage.setItem('pusher_config', JSON.stringify(pusherConfigForStorage));
        
        // Reinitialize Laravel Echo with new config after saving
        import('../services/laravelEcho').then(module => {
          const laravelEcho = module.default;
          laravelEcho.reinitialize();
        });
      }
      
      
      // Update configs via API
      await configService.updateConfigs(configsToUpdate);
      
      // Show success message based on tab
      const tabNames = {
        general: 'General',
        mail: 'Mail',
        aws: 'AWS',
        msg91: 'MSG91',
        pusher: 'Pusher',
        notifications: 'Email Preferences'
      };
      
      toast.success(`${tabNames[activeTab]} configuration saved successfully`);
      
      // Refresh settings to ensure we have the latest data
      await fetchSettings();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const tabs = [
    { id: 'general', label: 'General Configuration', icon: SettingsIcon },
    { id: 'mail', label: 'Mail Configuration', icon: Mail },
    { id: 'aws', label: 'AWS Configuration', icon: Cloud },
    { id: 'msg91', label: 'MSG91 Configuration', icon: MessageSquare },
    { id: 'pusher', label: 'Pusher Configuration', icon: Bell },
    { id: 'notifications', label: 'Email Notifications', icon: Mail }
  ];

  // Debug logging
  useEffect(() => {
  }, [generalSettings, mailSettings, awsSettings, msg91Settings, configsLoaded]);

  return (
    <div className="mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <SettingsIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">System Config</h1>
                <p className="text-sm text-gray-600">Configure mail, storage, and SMS settings</p>
              </div>
            </div>
            <button
              onClick={() => {
                fetchSettings();
              }}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {loading ? (
            <div className="p-6">
              <FormLoader rows={8} />
            </div>
          ) : (
            <>
              {/* General Settings */}
              {activeTab === 'general' && (
                <div className="p-6" key={`general-${configsLoaded}`}>
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">General Configuration</h2>
                    <p className="text-sm text-gray-600">Configure general system settings</p>
                  </div>

                  <div className="space-y-6">
                    {/* Watermark Font Size */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start gap-3 mb-4">
                        <FileText className="w-5 h-5 text-gray-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-base font-medium text-gray-900 mb-1">Watermark Font Size</h3>
                          <p className="text-sm text-gray-600">Configure the default font size for document watermarks (in pixels)</p>
                        </div>
                      </div>
                      
                      <div className="max-w-xs">
                        <input
                          type="number"
                          min="8"
                          max="72"
                          step="1"
                          value={generalSettings.WATERMARK_FONT_SIZE || '14'}
                          onChange={(e) => setGeneralSettings({...generalSettings, WATERMARK_FONT_SIZE: e.target.value})}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="14"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Font size range: 8-72 pixels (default: 14px)
                        </p>
                      </div>
                      
                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-xs text-gray-600">
                          <strong>Note:</strong> This setting controls the default font size for watermarks applied to documents. Larger values make watermarks more prominent.
                        </p>
                      </div>
                    </div>

                    {/* Two-Factor Authentication */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start gap-3 mb-4">
                        <Lock className="w-5 h-5 text-gray-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-base font-medium text-gray-900 mb-1">Two-Factor Authentication</h3>
                          <p className="text-sm text-gray-600">Enable or disable 2FA for all users in the system</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="twoFactorAuth"
                            checked={generalSettings['2FA_LOGIN_REQUIRED'] === true}
                            onChange={() => setGeneralSettings({...generalSettings, '2FA_LOGIN_REQUIRED': true})}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Enable 2FA</span>
                        </label>
                        
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="twoFactorAuth"
                            checked={generalSettings['2FA_LOGIN_REQUIRED'] === false}
                            onChange={() => setGeneralSettings({...generalSettings, '2FA_LOGIN_REQUIRED': false})}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Disable 2FA</span>
                        </label>
                      </div>
                      
                      {generalSettings['2FA_LOGIN_REQUIRED'] && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>Note:</strong> When enabled, all users will be required to set up two-factor authentication on their next login.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* NDA Text Editor */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start gap-3 mb-4">
                        <FileText className="w-5 h-5 text-gray-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-base font-medium text-gray-900 mb-1">NDA Agreement Text</h3>
                          <p className="text-sm text-gray-600">Configure the Non-Disclosure Agreement text that users must accept</p>
                        </div>
                      </div>
                      
                      <div className="border border-gray-300 rounded-lg overflow-hidden">
                        <RichTextEditor
                          value={generalSettings.NDA_TEXT || ''}
                          onChange={(value) => setGeneralSettings({...generalSettings, NDA_TEXT: value})}
                          placeholder="Enter your NDA agreement text here..."
                          placeholders={[]} // No placeholders for NDA
                        />
                      </div>
                      
                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-xs text-gray-600">
                          <strong>Tips:</strong> Use the rich text editor to format your NDA with:
                        </p>
                        <ul className="text-xs text-gray-600 mt-1 ml-4 list-disc">
                          <li>Headings (H1, H2, H3)</li>
                          <li>Bold, italic, underline, and strikethrough text</li>
                          <li>Ordered and unordered lists</li>
                          <li>Blockquotes and code blocks</li>
                          <li>Text and background colors</li>
                          <li>Links and images</li>
                          <li>Text alignment (left, center, right, justify)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Mail Settings */}
              {activeTab === 'mail' && (
                <div className="p-6" key={`mail-${configsLoaded}`}>
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Mail Configuration</h2>
                    <p className="text-sm text-gray-600">Configure SMTP settings for sending emails</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mail Mailer
                      </label>
                      <select
                        value={mailSettings.MAIL_MAILER || 'smtp'}
                        onChange={(e) => setMailSettings({...mailSettings, MAIL_MAILER: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="smtp">SMTP</option>
                        <option value="sendmail">Sendmail</option>
                        <option value="mailgun">Mailgun</option>
                        <option value="ses">Amazon SES</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mail Host
                      </label>
                      <input
                        type="text"
                        value={mailSettings.MAIL_HOST || ''}
                        onChange={(e) => setMailSettings({...mailSettings, MAIL_HOST: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="smtp.gmail.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mail Port
                      </label>
                      <input
                        type="text"
                        value={mailSettings.MAIL_PORT || ''}
                        onChange={(e) => setMailSettings({...mailSettings, MAIL_PORT: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="587"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mail Username
                      </label>
                      <input
                        type="text"
                        value={mailSettings.MAIL_USERNAME || ''}
                        onChange={(e) => setMailSettings({...mailSettings, MAIL_USERNAME: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="your-email@gmail.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mail Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.mailPassword ? 'text' : 'password'}
                          value={mailSettings.MAIL_PASSWORD || ''}
                          onChange={(e) => setMailSettings({...mailSettings, MAIL_PASSWORD: e.target.value})}
                          className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('mailPassword')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 z-10"
                        >
                          {showPasswords.mailPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Address
                      </label>
                      <input
                        type="email"
                        value={mailSettings.MAIL_FROM_ADDRESS || ''}
                        onChange={(e) => setMailSettings({...mailSettings, MAIL_FROM_ADDRESS: e.target.value.toLowerCase()})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="noreply@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Name
                      </label>
                      <input
                        type="text"
                        value={mailSettings.MAIL_FROM_NAME || ''}
                        onChange={(e) => setMailSettings({...mailSettings, MAIL_FROM_NAME: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Lockbox Admin"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Encryption
                      </label>
                      <select
                        value={mailSettings.MAIL_ENCRYPTION || 'TLS'}
                        onChange={(e) => setMailSettings({...mailSettings, MAIL_ENCRYPTION: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="TLS">TLS</option>
                        <option value="SSL">SSL</option>
                        <option value="">None</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* AWS Settings */}
              {activeTab === 'aws' && (
                <div className="p-6" key={`aws-${configsLoaded}`}>
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">AWS Configuration</h2>
                    <p className="text-sm text-gray-600">Configure AWS S3 settings for file storage</p>
                    
                    {/* Security Notice */}
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                      <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800">
                        <strong>Security Notice:</strong> AWS credentials are sensitive information. 
                        Ensure you're using IAM roles with minimal required permissions and never share these credentials.
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Access Key ID
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.awsAccessKey ? 'text' : 'password'}
                          value={awsSettings.AWS_ACCESS_KEY_ID || ''}
                          onChange={(e) => setAwsSettings({...awsSettings, AWS_ACCESS_KEY_ID: e.target.value})}
                          className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="AKIAIOSFODNN7EXAMPLE"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('awsAccessKey')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 z-10"
                        >
                          {showPasswords.awsAccessKey ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Secret Access Key
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.awsSecret ? 'text' : 'password'}
                          value={awsSettings.AWS_SECRET_ACCESS_KEY || ''}
                          onChange={(e) => setAwsSettings({...awsSettings, AWS_SECRET_ACCESS_KEY: e.target.value})}
                          className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('awsSecret')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 z-10"
                        >
                          {showPasswords.awsSecret ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Region
                      </label>
                      <input
                        type="text"
                        value={awsSettings.AWS_DEFAULT_REGION || ''}
                        onChange={(e) => setAwsSettings({...awsSettings, AWS_DEFAULT_REGION: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="us-east-1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bucket Name
                      </label>
                      <input
                        type="text"
                        value={awsSettings.AWS_BUCKET || ''}
                        onChange={(e) => setAwsSettings({...awsSettings, AWS_BUCKET: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="my-bucket-name"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={awsSettings.AWS_USE_PATH_STYLE_ENDPOINT || false}
                          onChange={(e) => setAwsSettings({...awsSettings, AWS_USE_PATH_STYLE_ENDPOINT: e.target.checked})}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Use Path Style Endpoint</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        Enable this for S3-compatible services like MinIO
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* MSG91 Settings */}
              {activeTab === 'msg91' && (
                <div className="p-6" key={`msg91-${configsLoaded}`}>
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">MSG91 Configuration</h2>
                    <p className="text-sm text-gray-600">Configure MSG91 settings for SMS notifications</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Auth Key
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.msg91Auth ? 'text' : 'password'}
                          value={msg91Settings.MSG91_AUTH_KEY || ''}
                          onChange={(e) => setMsg91Settings({...msg91Settings, MSG91_AUTH_KEY: e.target.value})}
                          className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('msg91Auth')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 z-10"
                        >
                          {showPasswords.msg91Auth ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sender ID
                      </label>
                      <input
                        type="text"
                        value={msg91Settings.MSG91_SENDER_ID || ''}
                        onChange={(e) => setMsg91Settings({...msg91Settings, MSG91_SENDER_ID: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="LOCKBX"
                        maxLength="6"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Route
                      </label>
                      <select
                        value={msg91Settings.MSG91_ROUTE || '4'}
                        onChange={(e) => setMsg91Settings({...msg91Settings, MSG91_ROUTE: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="1">Promotional</option>
                        <option value="4">Transactional</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country Code
                      </label>
                      <input
                        type="text"
                        value={msg91Settings.MSG91_COUNTRY || '91'}
                        onChange={(e) => setMsg91Settings({...msg91Settings, MSG91_COUNTRY: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="91"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Pusher Settings */}
              {activeTab === 'pusher' && (
                <div className="p-6" key={`pusher-${configsLoaded}`}>
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Pusher Configuration</h2>
                    <p className="text-sm text-gray-600">Configure Pusher settings for real-time notifications</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        App ID
                      </label>
                      <input
                        type="text"
                        value={pusherSettings.PUSHER_APP_ID || ''}
                        onChange={(e) => setPusherSettings({...pusherSettings, PUSHER_APP_ID: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="1234567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        App Key
                      </label>
                      <input
                        type="text"
                        value={pusherSettings.PUSHER_APP_KEY || ''}
                        onChange={(e) => setPusherSettings({...pusherSettings, PUSHER_APP_KEY: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="your-app-key"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        App Secret
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.pusher_secret ? 'text' : 'password'}
                          value={pusherSettings.PUSHER_APP_SECRET || ''}
                          onChange={(e) => setPusherSettings({...pusherSettings, PUSHER_APP_SECRET: e.target.value})}
                          className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="your-app-secret"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({...prev, pusher_secret: !prev.pusher_secret}))}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPasswords.pusher_secret ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cluster
                      </label>
                      <select
                        value={pusherSettings.PUSHER_APP_CLUSTER || 'mt1'}
                        onChange={(e) => setPusherSettings({...pusherSettings, PUSHER_APP_CLUSTER: e.target.value})}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="mt1">mt1 (US East - N. Virginia)</option>
                        <option value="us2">us2 (US East - Ohio)</option>
                        <option value="us3">us3 (US West - Oregon)</option>
                        <option value="eu">eu (EU - Ireland)</option>
                        <option value="ap1">ap1 (Asia Pacific - Singapore)</option>
                        <option value="ap2">ap2 (Asia Pacific - Mumbai)</option>
                        <option value="ap3">ap3 (Asia Pacific - Tokyo)</option>
                        <option value="ap4">ap4 (Asia Pacific - Sydney)</option>
                        <option value="sa1">sa1 (South America - São Paulo)</option>
                      </select>
                    </div>

                    <div className="col-span-2">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="pusher-use-tls"
                          checked={pusherSettings.PUSHER_USE_TLS === true}
                          onChange={(e) => setPusherSettings({...pusherSettings, PUSHER_USE_TLS: e.target.checked})}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="pusher-use-tls" className="text-sm font-medium text-gray-700">
                          Use TLS/SSL Connection
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 ml-7">
                        Enable secure TLS/SSL connection for Pusher (recommended for production).
                      </p>
                    </div>

                    {/* Notification Test Section */}
                    <div className="col-span-2 border-t pt-6">
                      
                      {/* Browser Notification Test Section */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                        <div className="flex items-start gap-3">
                          <Bell className="w-5 h-5 text-green-600 mt-0.5" />
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Test Browser Notification</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              Send browser notifications to multiple users. Enter email addresses separated by commas (optional).
                            </p>
                            <input
                              type="text"
                              id="browser-recipient-emails"
                              placeholder="Leave empty for current user only, or enter: admin@example.com, user@example.com"
                              className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 text-sm mb-3"
                            />
                            <button
                              onClick={async () => {
                                setTestingBrowserNotification(true);
                                try {
                                  
                                  // Get recipient emails if provided
                                  const emailInput = document.getElementById('browser-recipient-emails');
                                  const emails = emailInput.value.split(',').map(e => e.trim()).filter(e => e);
                                  
                                  // Check if browser notifications are supported
                                  if (!('Notification' in window)) {
                                    toast.error('Browser notifications are not supported in this browser');
                                    setTestingBrowserNotification(false);
                                    return;
                                  }
                                  
                                  // Check and request permission if needed
                                  let permission = Notification.permission;
                                  
                                  if (permission === 'default') {
                                    // Request permission first
                                    toast('Requesting notification permission...', { icon: 'ℹ️' });
                                    permission = await Notification.requestPermission();
                                    
                                    if (permission !== 'granted') {
                                      toast('Browser notification permission was denied, but will still send via Pusher', { icon: '⚠️' });
                                    } else {
                                      toast.success('Permission granted!');
                                    }
                                  } else if (permission === 'denied') {
                                    toast('Browser notifications are blocked, but will still send via Pusher', { icon: '⚠️' });
                                  }
                                  
                                  // Continue with sending notification regardless of browser permission
                                  // Pusher notifications can still work even if browser notifications are blocked
                                  
                                  // Determine recipients
                                  let recipientsToSend = [];
                                  let recipientMessage = '';
                                  
                                  if (emails.length > 0) {
                                    // Multiple users specified
                                    recipientsToSend = emails;
                                    recipientMessage = `to ${emails.length} user(s)`;
                                  } else {
                                    // No emails provided, send to current user via API
                                    // Pass empty array to let pusherService handle current user
                                    recipientsToSend = [];
                                    recipientMessage = 'to current user';
                                    const currentUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
                                  }
                                  
                                  // Send notification via API/Pusher
                                  try {
                                    // Get current user
                                    const currentUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
                                    
                                    if (!currentUser?.id) {
                                      toast.error('No user found. Please login again.');
                                      return;
                                    }
                                    
                                    // Step 1: Setup Pusher subscription
                                    toast('Setting up notification channel...', { icon: '🔔' });
                                    
                                    try {
                                      // Import laravelEcho service
                                      const laravelEchoModule = await import('../services/laravelEcho');
                                      const laravelEcho = laravelEchoModule.default;
                                      
                                      // Initialize Echo if needed
                                      let echo = laravelEcho.getInstance();
                                      if (!echo) {
                                        echo = laravelEcho.initialize();
                                      }
                                      
                                      if (!echo) {
                                        toast.error('Pusher is not configured. Please configure Pusher settings first.');
                                        return;
                                      }
                                      
                                      // Subscribe to user channel
                                      const channelName = `App.User.${currentUser.id}`;

                                      // Subscribe to private channel
                                      const channel = echo.private(channelName);
                                      
                                      // Remove any existing listeners first
                                      channel.stopListening('.TestPROD');
                                      
                                      // Add fresh listener for TestPROD event
                                      channel.listen('.TestPROD', (data) => {
                                        
                                        // Show browser notification
                                        if (Notification.permission === 'granted') {
                                          try {
                                            const notification = new Notification(data.title || 'Test Notification', {
                                              body: data.body || data.message || 'Notification received via Pusher',
                                              icon: '/favicon.ico',
                                              tag: 'test-notification-' + Date.now()
                                            });
                                            
                                            notification.onclick = () => {
                                              window.focus();
                                              notification.close();
                                            };
                                          } catch (err) {
                                          }
                                        }
                                        
                                        // Show toast notification
                                        toast.success(data.body || data.message || 'Notification received!');
                                      });
                                      
                                      // Use the ensureUserChannelSubscribed method we created
                                      const isSubscribed = await laravelEcho.ensureUserChannelSubscribed(currentUser.id);
                                      
                                      if (isSubscribed) {
                                        toast.success('Channel subscribed successfully!');
                                      } else {
                                        toast('Channel subscription uncertain, but attempting to send...', { icon: '⚠️' });
                                      }
                                      
                                      // Wait a bit more to ensure subscription is fully ready
                                      await new Promise(resolve => setTimeout(resolve, 1000));
                                      
                                    } catch (subscriptionError) {
                                      toast('Subscription setup failed, but will try to send anyway...', { icon: '⚠️' });
                                    }
                                    
                                    // Step 2: Send the notification via API
                                    toast('Sending notification...', { icon: '📤' });
                                    
                                    // Prepare parameters
                                    const emailsToSend = recipientsToSend.length > 0 ? recipientsToSend : [];
                                    const userIdToSend = recipientsToSend.length === 0 ? currentUser.id : null;
                                    
                                    
                                    // Call the API
                                    const response = await configService.testPusherNotification(emailsToSend, userIdToSend);
                                    
                                    
                                    // If we get a response without throwing an error, consider it successful
                                    
                                    if (response && !response.error) {
                                      toast.success(`Browser notification sent ${recipientMessage}!`);
                                      
                                      // Show local confirmation notification
                                      const localNotif = new Notification('📤 Notification Sent', {
                                        body: `Successfully sent browser notification ${recipientMessage}`,
                                        icon: '/favicon.ico',
                                        badge: '/favicon.ico',
                                        tag: 'notification-confirmation-' + Date.now()
                                      });
                                      
                                      setTimeout(() => localNotif.close(), 5000);
                                    } else {
                                      toast.error(response?.message || response?.error || `Failed to send notification ${recipientMessage}`);
                                    }
                                  } catch (apiError) {
                                    toast.error(`Failed to send notification ${recipientMessage}. Check console for details.`);
                                    
                                    // Fallback: Show local notification only (without API)
                                    
                                    try {
                                      const fallbackNotif = new Notification('🎉 Test Browser Notification (Local)', {
                                        body: 'API call failed, but browser notifications are working! This is a local-only notification.',
                                        icon: '/favicon.ico',
                                        badge: '/favicon.ico',
                                        tag: 'test-notification-fallback-' + Date.now(),
                                        requireInteraction: false
                                      });
                                      
                                      fallbackNotif.onclick = () => {
                                        window.focus();
                                        fallbackNotif.close();
                                      };
                                      
                                      toast('API failed, but local browser notification works!', { icon: '⚠️' });
                                    } catch (fallbackError) {
                                      toast.error('Both API and local notifications failed.');
                                    }
                                  }
                                } catch (error) {
                                  toast.error(`Error: ${error.message || 'Unknown error occurred'}`);
                                } finally {
                                  setTestingBrowserNotification(false);
                                }
                              }}
                              disabled={testingBrowserNotification}
                              className={`flex items-center gap-2 px-3 py-1.5 focus:border-transparent text-sm font-medium text-white rounded-lg transition-colors ${
                                testingBrowserNotification
                                  ? 'bg-gray-400 cursor-not-allowed'
                                  : 'bg-green-600 hover:bg-green-700'
                              }`}
                            >
                              {testingBrowserNotification ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Testing...
                                </>
                              ) : (
                                <>
                                  <Bell className="w-4 h-4" />
                                  Test Browser Notification
                                </>
                              )}
                            </button>
                            <p className="text-xs text-gray-500 mt-2">
                              Sends notifications via Pusher API. Leave empty to send to yourself, or add emails to send to multiple users.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Email Notification Settings */}
              {activeTab === 'notifications' && (
                <div className="p-6" key={`notifications-${configsLoaded}`}>
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Email Notification Configuration</h2>
                    <p className="text-sm text-gray-600">Configure global email notification settings for the system</p>
                  </div>

                  <div className="space-y-6">
                    {/* Email Notifications Enabled */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start gap-3 mb-4">
                        <Bell className="w-5 h-5 text-gray-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-base font-medium text-gray-900 mb-1">Email Notifications</h3>
                          <p className="text-sm text-gray-600">Enable or disable email notifications globally</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="emailNotifications"
                            checked={notificationSettings.EMAIL_NOTIFICATIONS_ENABLED === true}
                            onChange={() => setNotificationSettings({...notificationSettings, EMAIL_NOTIFICATIONS_ENABLED: true})}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Enable</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="emailNotifications"
                            checked={notificationSettings.EMAIL_NOTIFICATIONS_ENABLED === false}
                            onChange={() => setNotificationSettings({...notificationSettings, EMAIL_NOTIFICATIONS_ENABLED: false})}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Disable</span>
                        </label>
                      </div>

                      {notificationSettings.EMAIL_NOTIFICATIONS_ENABLED === true && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>Note:</strong> When enabled, users will receive email notifications based on the frequency settings below.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Notification Frequency */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start gap-3 mb-4">
                        <Clock className="w-5 h-5 text-gray-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-base font-medium text-gray-900 mb-1">Notification Frequency</h3>
                          <p className="text-sm text-gray-600">Set the default frequency for email notifications</p>
                        </div>
                      </div>

                      <div className="max-w-xs">
                        <select
                          value={notificationSettings.EMAIL_NOTIFICATIONS_FREQUENCY || '24h'}
                          onChange={(e) => setNotificationSettings({...notificationSettings, EMAIL_NOTIFICATIONS_FREQUENCY: e.target.value})}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          disabled={notificationSettings.EMAIL_NOTIFICATIONS_ENABLED !== true}
                        >
                          <option value="1h">Every Hour</option>
                          <option value="6h">Every 6 Hours</option>
                          <option value="12h">Every 12 Hours</option>
                          <option value="24h">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="15d">Every 15 Days</option>
                          <option value="monthly">Monthly</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          This sets the default frequency. Users can override this in their personal settings.
                        </p>
                      </div>
                    </div>

                    {/* Time of Day */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start gap-3 mb-4">
                        <Clock className="w-5 h-5 text-gray-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-base font-medium text-gray-900 mb-1">Preferred Time</h3>
                          <p className="text-sm text-gray-600">Set the default time of day for daily email notifications</p>
                        </div>
                      </div>

                      <div className="max-w-xs">
                        <input
                          type="time"
                          value={notificationSettings.EMAIL_NOTIFICATIONS_TIME_OF_DAY || '09:00'}
                          onChange={(e) => setNotificationSettings({...notificationSettings, EMAIL_NOTIFICATIONS_TIME_OF_DAY: e.target.value})}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          disabled={notificationSettings.EMAIL_NOTIFICATIONS_ENABLED !== true}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Time format: HH:MM (24-hour format). This applies to daily notifications.
                        </p>
                      </div>

                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-xs text-gray-600">
                          <strong>Note:</strong> This time setting is used as the default for daily notifications. It doesn't affect hourly or other frequency settings.
                        </p>
                      </div>
                    </div>

                    {/* Information Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold mb-2">Email Notification System</p>
                          <ul className="space-y-1 list-disc list-inside">
                            <li>These are global default settings for all users</li>
                            <li>Individual users can override these settings in their profile</li>
                            <li>Notifications are batched and sent based on the frequency setting</li>
                            <li>Email notifications require proper mail configuration to work</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>Changes will take effect immediately after saving</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={fetchSettings}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>
                  <SaveButton
                    onClick={handleSave}
                    disabled={saving}
                    loading={saving}
                  >
                    Save Changes
                  </SaveButton>
                </div>
              </div>
            </>
          )}
        </div>
    </div>
  );
};

export default SystemConfig;